import React, { useState, useEffect, useCallback } from 'react';
import { socialPartners } from '../data/socialPartners';
import { AddPartnersModal } from './AddPartnersModal';
import { CreateSocialPartnerScenarioModal } from './CreateSocialPartnerScenarioModal';
import { LoadingModal } from './LoadingModal';

export function SocialPartnersOptimizer() {
  // Load scenarios from localStorage or use default
  const [scenarios, setScenarios] = useState(() => {
    const saved = localStorage.getItem('socialScenarios');
    if (saved) {
      return JSON.parse(saved);
    }
    return [{
      id: 'base',
      name: 'Base Scenario',
      client: 'Amazon Inc',
      brand: 'Prime Video',
      dateRange: 'Q4 2024',
      budget: '$500,000',
      audience: {
        survey: 'Demo Survey',
        segment: 'Early adopters 35-44'
      },
      partners: []
    }];
  });

  const [currentScenario, setCurrentScenario] = useState('base');
  const [showNewScenarioModal, setShowNewScenarioModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Get selected partners for current scenario
  const [selectedPartners, setSelectedPartners] = useState(() => {
    const scenario = scenarios.find(s => s.id === currentScenario);
    return scenario?.partners || [];
  });

  // Combine the effects into one that handles both localStorage and partner updates
  useEffect(() => {
    // Get current scenario's partners when switching tabs
    const scenario = scenarios.find(s => s.id === currentScenario);
    setSelectedPartners(scenario?.partners || []);
    
    // Save to localStorage
    localStorage.setItem('socialScenarios', JSON.stringify(scenarios));
  }, [currentScenario, scenarios]);

  // Remove the effect that was updating scenarios when partners changed
  // Instead, update the scenarios directly when partners change
  const updatePartners = useCallback((newPartners) => {
    setSelectedPartners(newPartners);
    setScenarios(prev => prev.map(scenario => 
      scenario.id === currentScenario
        ? { ...scenario, partners: newPartners }
        : scenario
    ));
  }, [currentScenario]);

  const [showAddPartnersModal, setShowAddPartnersModal] = useState(false);

  const [metrics, setMetrics] = useState({
    totalCost: 0,
    totalImpressions: 0,
    totalReach: 0,
    performanceIndex: 0
  });

  // Move calculateMetrics up, before the useEffect that uses it
  const calculateMetrics = useCallback((partners) => {
    return partners.reduce((acc, partner) => {
      const basePartner = socialPartners.find(p => p.id === partner.id);
      const impressions = Math.floor(partner.budget * basePartner.impressionRate);
      const reach = Math.floor(partner.budget * basePartner.reachRate);
      
      return {
        totalCost: acc.totalCost + partner.budget,
        totalImpressions: acc.totalImpressions + impressions,
        totalReach: acc.totalReach + reach,
        performanceIndex: acc.performanceIndex + (basePartner.baseEfficiency * partner.budget)
      };
    }, { totalCost: 0, totalImpressions: 0, totalReach: 0, performanceIndex: 0 });
  }, []);

  // Then the useEffect that uses it
  useEffect(() => {
    const newMetrics = calculateMetrics(selectedPartners);
    setMetrics(newMetrics);
  }, [selectedPartners, calculateMetrics]);

  const handleNameChange = (e) => {
    if (e.key === 'Enter') {
      setScenarios(prev => prev.map(scenario => 
        scenario.id === currentScenario 
          ? { ...scenario, name: e.target.value }
          : scenario
      ));
      setIsEditing(false);
    }
  };

  // Update all partner-modifying functions to use updatePartners
  const handleDeletePartner = (partnerId) => {
    const newPartners = selectedPartners.filter(p => p.id !== partnerId);
    updatePartners(newPartners);
  };

  const handleBudgetChange = (partnerId, newBudget) => {
    const newPartners = selectedPartners.map(partner => 
      partner.id === partnerId 
        ? { ...partner, budget: parseInt(newBudget) }
        : partner
    );
    updatePartners(newPartners);
  };

  const handleAIRecommendations = () => {
    // Simulate AI recommendations based on efficiency and reach
    const recommendedPartners = [
      {
        ...socialPartners.find(p => p.id === 'tiktok_feed'),
        budget: 45000,
        isAIRecommended: true,
        reasoning: "High efficiency score and excellent reach for your target demographic. Recent performance data shows strong engagement rates."
      },
      {
        ...socialPartners.find(p => p.id === 'instagram_reels'),
        budget: 35000,
        isAIRecommended: true,
        reasoning: "Strong alignment with your brand's visual storytelling needs. Historical data shows good performance with similar campaigns."
      },
      {
        ...socialPartners.find(p => p.id === 'meta_feed'),
        budget: 30000,
        isAIRecommended: true,
        reasoning: "Active partnership deal available. Good balance of reach and efficiency for your budget range."
      }
    ];

    const existingIds = selectedPartners.map(p => p.id);
    const newRecommendations = recommendedPartners.filter(p => !existingIds.includes(p.id));
    const newPartners = [...selectedPartners, ...newRecommendations];
    updatePartners(newPartners);
  };

  const handleCreateNewScenario = (newScenario) => {
    setScenarios(prev => [...prev, {
      ...newScenario,
      partners: []  // Initialize empty partners array
    }]);
    setCurrentScenario(newScenario.id);
  };

  // Add new state for loading
  const [isOptimizing, setIsOptimizing] = useState(false);

  // Add the optimization handler
  const handleOptimize = () => {
    setIsOptimizing(true);
    
    setTimeout(() => {
      const currentScenarioData = scenarios.find(s => s.id === currentScenario);
      const scenarioBudget = parseInt(currentScenarioData.budget.replace(/[^0-9]/g, ''));
      const totalBudget = Math.max(scenarioBudget, metrics.totalCost);

      // Calculate efficiency-weighted distribution
      const totalEfficiency = selectedPartners.reduce((sum, partner) => {
        const basePartner = socialPartners.find(p => p.id === partner.id);
        return sum + basePartner.baseEfficiency;
      }, 0);

      // Distribute budget based on efficiency
      const optimizedPartners = selectedPartners.map(partner => {
        const basePartner = socialPartners.find(p => p.id === partner.id);
        const share = basePartner.baseEfficiency / totalEfficiency;
        const optimizedBudget = Math.floor(totalBudget * share);
        
        // Ensure budget is within min-max constraints
        const constrainedBudget = Math.min(
          Math.max(optimizedBudget, basePartner.minBudget),
          basePartner.maxBudget
        );

        return {
          ...partner,
          budget: constrainedBudget
        };
      });

      updatePartners(optimizedPartners);
      setIsOptimizing(false);
    }, 3000);
  };

  // Add this new function after handleCreateNewScenario
  const handleDeleteScenario = useCallback((scenarioId) => {
    const baseScenario = {
      id: 'base',
      name: 'Base Scenario',
      client: 'Amazon Inc',
      brand: 'Prime Video',
      dateRange: 'Q4 2024',
      budget: '$500,000',
      audience: {
        survey: 'Demo Survey',
        segment: 'Early adopters 35-44'
      },
      partners: []
    };

    setScenarios(prev => {
      const updatedScenarios = prev.filter(s => s.id !== scenarioId);
      const finalScenarios = updatedScenarios.length === 0 ? [baseScenario] : updatedScenarios;
      
      // If we deleted the current scenario, update the current scenario ID
      if (scenarioId === currentScenario) {
        const nextScenario = finalScenarios[0];
        setCurrentScenario(nextScenario.id);
      }
      
      return finalScenarios;
    });
  }, [currentScenario]);

  return (
    <main className="main-content">
      <div className="page-header">
        <h1 className="page-title">Social Partner Optimizer</h1>
        <button 
          className="create-scenario-button"
          onClick={() => setShowNewScenarioModal(true)}
        >
          Create New Scenario
        </button>
      </div>

      {/* Scenario Overview */}
      <div className="project-details-container">
        <div className="project-details">
          <div className="scenario-name-header">
            <div className="scenario-name-container">
              {isEditing ? (
                <input
                  type="text"
                  defaultValue={scenarios.find(s => s.id === currentScenario)?.name}
                  onKeyDown={handleNameChange}
                  autoFocus
                  className="scenario-name-input"
                />
              ) : (
                <h2 
                  className="details-title editable"
                  onClick={() => setIsEditing(true)}
                >
                  {scenarios.find(s => s.id === currentScenario)?.name}
                </h2>
              )}
              <button
                className="delete-scenario-button compact"
                onClick={() => handleDeleteScenario(currentScenario)}
                title="Delete scenario"
              >
                ×
              </button>
            </div>
          </div>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Client</span>
              <span className="summary-value compact">
                {scenarios.find(s => s.id === currentScenario)?.client}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Brand</span>
              <span className="summary-value compact">
                {scenarios.find(s => s.id === currentScenario)?.brand}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Date Range</span>
              <span className="summary-value compact">
                {scenarios.find(s => s.id === currentScenario)?.dateRange}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Budget</span>
              <span className="summary-value compact">
                {scenarios.find(s => s.id === currentScenario)?.budget}
              </span>
            </div>
          </div>
        </div>

        <div className="audience-details">
          <h2 className="details-title">Audience</h2>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Connection Survey</span>
              <span className="summary-value compact">
                {scenarios.find(s => s.id === currentScenario)?.audience.survey}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Audience 1</span>
              <span className="summary-value compact">
                {scenarios.find(s => s.id === currentScenario)?.audience.segment}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scenario Tabs */}
      <div className="scenario-tabs">
        {scenarios.map(scenario => (
          <button
            key={scenario.id}
            className={`scenario-tab ${currentScenario === scenario.id ? 'active' : ''}`}
            onClick={() => setCurrentScenario(scenario.id)}
          >
            {scenario.name}
          </button>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <div className="left-buttons">
          <button 
            className="button-primary"
            onClick={() => setShowAddPartnersModal(true)}
          >
            Add Partners
          </button>
          <button 
            className="button-secondary"
            onClick={handleAIRecommendations}
          >
            AI Recommendations ✨
          </button>
        </div>
        <button 
          className="button-primary"
          onClick={handleOptimize}
        >
          Optimize Partners
        </button>
      </div>

      {/* Metrics Summary */}
      <div className="metrics-summary">
        <div className="metric-card">
          <div className="metric-label">
            <span className="metric-icon">$</span>
            Total Cost
          </div>
          <div className="metric-value">${(metrics.totalCost/1000).toFixed(1)}K</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">
            <span className="metric-icon">👀</span>
            Total Impressions
          </div>
          <div className="metric-value">{(metrics.totalImpressions/1000000).toFixed(1)}M</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">
            <span className="metric-icon">👥</span>
            Total Reach
          </div>
          <div className="metric-value">{(metrics.totalReach/1000000).toFixed(1)}M</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">
            <span className="metric-icon">📈</span>
            Performance Index
          </div>
          <div className="metric-value">
            {(metrics.totalCost > 0 
              ? metrics.performanceIndex/metrics.totalCost 
              : 0).toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Partners Table */}
      <div className="partners-table">
        <div className="table-header">
          <div className="partner-col">Partner</div>
          <div className="budget-col">Budget Allocation</div>
          <div className="metrics-col">Impressions</div>
          <div className="metrics-col">Reach</div>
          <div className="metrics-col">Efficiency</div>
          <div className="actions-col"></div>
        </div>
        
        {selectedPartners.map(partner => {
          const basePartner = socialPartners.find(p => p.id === partner.id);
          const impressions = Math.floor(partner.budget * basePartner.impressionRate);
          const reach = Math.floor(partner.budget * basePartner.reachRate);
          
          return (
            <div className="table-row">
              <div className="table-row-content">
                <div className="partner-col">
                  <div className="partner-name">
                    {partner.name}
                    {partner.isDeal && (
                      <span className="deal-badge">
                        <span role="img" aria-label="flag">🚩</span>
                        {' '}Orion Deal
                      </span>
                    )}
                    {partner.isAIRecommended && (
                      <span className="ai-badge" title="Recommended by AI">
                        <span role="img" aria-label="sparkle">✨</span>
                        AI recommended
                      </span>
                    )}
                  </div>
                </div>
                <div className="budget-col">
                  <div className="budget-input-row">
                    <input
                      type="range"
                      min={basePartner.minBudget}
                      max={basePartner.maxBudget}
                      value={partner.budget}
                      onChange={(e) => handleBudgetChange(partner.id, e.target.value)}
                    />
                    <input
                      type="text"
                      value={partner.budget.toLocaleString()}
                      onChange={(e) => {
                        const value = parseInt(e.target.value.replace(/,/g, ''));
                        if (!isNaN(value) && value <= 999999999) {
                          handleBudgetChange(partner.id, value);
                        }
                      }}
                      onBlur={(e) => {
                        // Ensure the value is within min-max constraints on blur
                        const value = parseInt(e.target.value.replace(/,/g, ''));
                        if (!isNaN(value)) {
                          const constrainedValue = Math.min(
                            Math.max(value, basePartner.minBudget),
                            Math.min(basePartner.maxBudget, 999999999)
                          );
                          handleBudgetChange(partner.id, constrainedValue);
                        }
                      }}
                      className="budget-input"
                    />
                  </div>
                  <div className="budget-range">
                    Min: ${basePartner.minBudget/1000}K | Max: ${basePartner.maxBudget/1000}K
                  </div>
                </div>
                <div className="metrics-col">{(impressions/1000).toFixed(1)}K</div>
                <div className="metrics-col">{(reach/1000).toFixed(1)}K</div>
                <div className="metrics-col">
                  <span className={`efficiency ${basePartner.baseEfficiency >= 80 ? 'high' : 'medium'}`}>
                    {basePartner.baseEfficiency}%
                  </span>
                </div>
              </div>
              {partner.isAIRecommended && partner.reasoning && (
                <div className="ai-reasoning">
                  <details>
                    <summary>View AI reasoning</summary>
                    <p>{partner.reasoning}</p>
                  </details>
                </div>
              )}
              <div className="actions-col">
                <button
                  className="delete-partner-button"
                  onClick={() => handleDeletePartner(partner.id)}
                  title="Remove partner"
                >
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showAddPartnersModal && (
        <AddPartnersModal 
          onClose={() => setShowAddPartnersModal(false)}
          existingPartners={selectedPartners.map(p => p.id)}
          availablePartners={socialPartners}
          onSelect={(partners) => {
            const newPartnersToAdd = partners.map(id => {
              const partnerData = socialPartners.find(p => p.id === id);
              return {
                ...partnerData,
                budget: 0,  // Start with zero budget for manually added partners
                isAIRecommended: false
              };
            });
            
            // Combine existing partners with new ones
            const combinedPartners = [...selectedPartners, ...newPartnersToAdd];
            updatePartners(combinedPartners);
            setShowAddPartnersModal(false);
          }}
        />
      )}

      {showNewScenarioModal && (
        <CreateSocialPartnerScenarioModal
          onClose={() => setShowNewScenarioModal(false)}
          onCreate={(newScenario) => {
            handleCreateNewScenario(newScenario);
            setShowNewScenarioModal(false);
          }}
        />
      )}

      {isOptimizing && (
        <LoadingModal message="Optimizing budgets..." />
      )}
    </main>
  );
} 